// Prefetch + local persistence layer for the iPad kiosk flow.
//
// Two-tier cache:
//   1. In-memory `Map` — instant reads inside the current tab.
//   2. `localStorage` — survives a hard refresh / device sleep so
//      returning iPads paint the menu tiles on the FIRST frame after
//      re-launch instead of waiting for a network round-trip.
//
// Also owns eager IMAGE warm-up: after treatments / crew / cabin JSON
// lands, we iterate their `image_path` fields and fire `new Image()` so
// the browser memory-cache holds the pixels by the time <img> tags
// mount downstream. This is the piece that fixed the "photos take
// significant time to load" report — the JSON was already warm but the
// image bytes weren't queued until the user actually reached the menu.
//
// A background revalidator keeps localStorage fresh while the iPad is
// idle so mid-charter content edits by the crew reach the kiosk within
// ~60 s even if the WebSocket dropped and no push arrived.
// Need `prewarmBlobImages` for the non-blocking image warmup path so
// the overlay can resolve as soon as JSON + session are ready and the
// photos stream into IDB in the background.
import { api, fileUrl } from "@/lib/api";
import { getBlobUrl, prewarmBlobImages } from "@/lib/imageBlobCache";

const TTL_MS = 30_000;              // In-memory freshness window
const LS_MAX_AGE_MS = 24 * 3600_000; // localStorage entries auto-expire after 24 h
const LS_PREFIX = "yspa_prefetch_v2:";

const store = new Map();

function key(path, params) {
  if (!params) return path;
  try {
    const ordered = Object.keys(params).sort().map((k) => `${k}=${params[k]}`).join("&");
    return `${path}?${ordered}`;
  } catch { return path; }
}

function lsRead(k) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + k);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j || typeof j.at !== "number") return null;
    if (Date.now() - j.at > LS_MAX_AGE_MS) return null;
    return j;
  } catch { return null; }
}

function lsWrite(k, data) {
  try {
    localStorage.setItem(LS_PREFIX + k, JSON.stringify({ data, at: Date.now() }));
  } catch { /* quota / private mode — ignore */ }
}

export function prime(path, data, params) {
  const k = key(path, params);
  store.set(k, { data, at: Date.now() });
  lsWrite(k, data);
}

// `readFresh` — in-memory hit that respects TTL. Returns null on miss.
// Used by pages that want the freshest snapshot for initial render.
export function readFresh(path, params) {
  const k = key(path, params);
  const entry = store.get(k);
  if (entry && Date.now() - entry.at <= TTL_MS) return entry.data;
  return null;
}

// `readAny` — in-memory + localStorage fallback, ignoring the 30 s TTL.
// Used to hydrate a page instantly on iPad re-launch: paint whatever we
// last saw, then let the normal fetch revalidate in the background.
// Consumers should still call the API on mount so the UI corrects
// itself if content changed while the device was asleep.
export function readAny(path, params) {
  const k = key(path, params);
  const entry = store.get(k);
  if (entry) return entry.data;
  const ls = lsRead(k);
  if (ls) {
    store.set(k, { data: ls.data, at: 0 }); // stale in-memory copy — revalidate on next call
    return ls.data;
  }
  return null;
}

export function invalidate(path, params) {
  const k = key(path, params);
  store.delete(k);
  try { localStorage.removeItem(LS_PREFIX + k); } catch { /* ignore */ }
}

export function clearAll() {
  store.clear();
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(LS_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

export async function prefetch(path, params) {
  try {
    const { data } = await api.get(path, params ? { params } : undefined);
    prime(path, data, params);
    return data;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Image warm-up
// ---------------------------------------------------------------------------
// Feeds `new Image()` into the browser memory-cache so downstream <img>
// tags render from cache without a fresh network round-trip. We keep a
// tiny module-level `Set` of paths we've already fired to avoid
// double-requesting the same URL during a single kiosk session.
const warmedImages = new Set();

export function prefetchImage(path) {
  if (!path || warmedImages.has(path)) return;
  warmedImages.add(path);
  try {
    const img = new Image();
    // decode="async" doesn't block the main thread; loading="eager"
    // hints the browser to request immediately even though the
    // element is never attached to the DOM.
    img.decoding = "async";
    img.loading = "eager";
    // Some Safari builds need fetchpriority for the priority hint.
    try { img.fetchPriority = "high"; } catch { /* ignore */ }
    img.src = fileUrl(path);
  } catch { /* ignore */ }
}

function warmImagesFrom(list, keys = ["image_path"]) {
  if (!Array.isArray(list)) return;
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    for (const k of keys) {
      if (item[k]) prefetchImage(item[k]);
    }
    // Cabin galleries carry an `image_paths` array too.
    if (Array.isArray(item.image_paths)) {
      for (const p of item.image_paths) prefetchImage(p);
    }
    if (item.cover_image_path) prefetchImage(item.cover_image_path);
  }
}

// ---------------------------------------------------------------------------
// Kiosk-wide warm-up
// ---------------------------------------------------------------------------
// Fires all four public GETs in parallel AND — as each one lands —
// eagerly requests the referenced image files. Called by:
//   • GuestLanding immediately after `/public/ipad-login` succeeds so
//     bytes hit the wire while React is still swapping routes.
//   • The background revalidator (see below) every 60 s so late-added
//     photos land on the kiosk without a manual reload.
export async function prefetchGuestKiosk() {
  const [treatments, crew, categories, settings] = await Promise.all([
    prefetch("/public/treatments"),
    prefetch("/public/crew"),
    prefetch("/public/treatment-categories"),
    prefetch("/public/settings"),
  ]);
  // Treatments carry `image_path`; crew carry `image_path`.
  warmImagesFrom(treatments || []);
  warmImagesFrom(crew || []);
  return { treatments, crew, categories, settings };
}

// Progress-reporting variant used by GuestLanding to render a
// "Preparing your cabin experience" loading screen after iPad login.
// Awaits every image warm-up (memory cache + IndexedDB blob store) and
// reports incremental progress so the crew see the bar advance step
// by step. Guarantees that by the time this resolves, every treatment
// tile, crew avatar and cabin cover is either fully in the browser
// memory cache OR persisted as a blob in IDB — the guest never sees a
// loading spinner on a tile mid-charter.
//
//   { extraImagePaths: string[] } — cabin cover / guest photos / etc.
//   { ipadCredentials: { cabin_id, session_token } } — when passed we
//     also POST /public/ipad/session and cache the response so
//     CabinView paints instantly from cache instead of showing
//     "Loading cabin…" during the route transition.
//   { blockOnImages: bool } — when false (P0 fix), the JSON + session
//     phases resolve first and images warm in the background via
//     `prewarmBlobImages`. This unblocks iPad boot from the ~180 MB
//     worth of treatment / crew / cabin photos and shortens the
//     "Preparing your cabin experience" screen to <1 s on a warm pod.
//   onProgress({ phase, loaded, total, pct })  — pct is the OVERALL
//     percentage across data + session + images (0-100). The step
//     label is separate so consumers can drive both the bar (pct)
//     and the sub-heading (step) from one callback pair.
//   onStep(label)                              — human-readable step
export async function prefetchGuestKioskWithProgress({
  extraImagePaths = [],
  ipadCredentials = null,
  blockOnImages = true,
  onProgress = () => {},
  onStep = () => {},
} = {}) {
  // Unified counter — every JSON endpoint, every image, and the
  // ipad-session refresh each contribute one tick. Total is patched
  // once we know how many images we're warming.
  let stepsDone = 0;
  const jsonEndpoints = [
    "/public/treatments",
    "/public/crew",
    "/public/treatment-categories",
    "/public/settings",
  ];
  // Baseline: 4 JSON + (1 session refresh if we have credentials).
  // Image count is added once we've seen the treatments/crew payloads.
  let stepsTotal = jsonEndpoints.length + (ipadCredentials ? 1 : 0);
  const tick = (extraLabel) => {
    stepsDone += 1;
    const pct = Math.min(100, Math.round((stepsDone / Math.max(1, stepsTotal)) * 100));
    if (extraLabel) onStep(extraLabel);
    onProgress({ phase: "combined", loaded: stepsDone, total: stepsTotal, pct });
  };

  // Phase 1 — data. Four small JSON payloads in parallel.
  onStep("Loading spa menu…");
  onProgress({ phase: "data", loaded: 0, total: stepsTotal, pct: 0 });
  const [treatments, crew, categories, settings] = await Promise.all(
    jsonEndpoints.map(async (path) => {
      const r = await prefetch(path);
      tick(); // silent — step label stays "Loading spa menu…"
      return r;
    })
  );

  // Phase 2 — iPad session refresh (if we have credentials). This is
  // what fills CabinView with roster + bookings + journey so the
  // "Loading cabin…" flash disappears completely on navigate.
  if (ipadCredentials?.cabin_id && ipadCredentials?.session_token) {
    onStep("Syncing your cabin roster…");
    try {
      const { data } = await api.post("/public/ipad/session", {
        cabin_id: ipadCredentials.cabin_id,
        session_token: ipadCredentials.session_token,
      });
      // Cache under a cabin-scoped key so CabinView can pluck it out
      // and skip its own initial spinner.
      prime(`ipad:session:${ipadCredentials.cabin_id}`, data);
    } catch (e) { /* soft-fail — CabinView will do its own fetch */ }
    tick();
  }

  // Phase 3 — image bytes. Now that we know how many, patch the total.
  const imagePaths = new Set();
  for (const t of (treatments || [])) if (t && t.image_path) imagePaths.add(t.image_path);
  for (const c of (crew || [])) if (c && c.image_path) imagePaths.add(c.image_path);
  for (const p of extraImagePaths) if (p) imagePaths.add(p);
  const paths = Array.from(imagePaths);
  const total = paths.length;

  // P0 fast-boot: when the caller opts out of blocking on images
  // (GuestLanding does — 52 cabin/treatment/crew photos at ~3.5 MB
  // average used to hold the "Preparing your cabin" screen for up to
  // 20 s on yacht WiFi). Fire the memory-cache prime + IDB prewarm
  // and return immediately. BlobImage races cache-vs-direct so tiles
  // still paint the moment the guest reaches /menu.
  if (!blockOnImages) {
    if (total > 0) {
      onStep("Ready — photos loading in background");
      for (const path of paths) prefetchImage(path);
      // Non-awaited — resolves whenever, individual failures are silent.
      prewarmBlobImages(paths);
    }
    onProgress({ phase: "combined", loaded: stepsDone, total: Math.max(1, stepsDone), pct: 100 });
    return { treatments, crew, categories, settings };
  }

  stepsTotal += total;
  // Re-emit progress so the bar smoothly re-normalises the moment we
  // know the real total (otherwise it would appear to jump backwards
  // when the denominator inflates).
  const pctAfterData = Math.round((stepsDone / Math.max(1, stepsTotal)) * 100);
  onProgress({ phase: "images", loaded: stepsDone, total: stepsTotal, pct: pctAfterData });
  onStep(total > 0 ? `Loading photos (0 of ${total})…` : "Almost ready…");
  if (total > 0) {
    let imgLoaded = 0;
    // Race each blob fetch against a 6 s per-image timeout so ONE
    // flaky photo never blocks the whole loading screen. The
    // memory-cache prime (`prefetchImage`) has already fired
    // unconditionally, so a timed-out photo still ends up in the
    // browser cache — the guest just won't have the persistent IDB
    // copy of that specific file.
    const withTimeout = (promise, ms) => new Promise((resolve) => {
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; resolve(null); } }, ms);
      promise.then((v) => { if (!done) { done = true; clearTimeout(timer); resolve(v); } })
             .catch(() => { if (!done) { done = true; clearTimeout(timer); resolve(null); } });
    });
    await Promise.all(paths.map(async (path) => {
      prefetchImage(path);
      await withTimeout(getBlobUrl(path), 6000);
      imgLoaded += 1;
      tick(`Loading photos (${imgLoaded} of ${total})…`);
    }));
  }
  return { treatments, crew, categories, settings };
}

// ---------------------------------------------------------------------------
// Background revalidator
// ---------------------------------------------------------------------------
// The iPad WebSocket is the primary sync channel — settings /
// treatment / crew edits push `settings.*` frames that the pages
// already consume. This tick is the belt-and-braces path for the rare
// case where the socket has silently dropped: every 60 s we refetch
// the guest-facing catalogue and re-warm its images. Cheap; the
// endpoint responses are small and the images already live in memory.
let backgroundTimerId = null;
export function startGuestBackgroundSync(intervalMs = 60_000) {
  if (backgroundTimerId) return () => stopGuestBackgroundSync();
  // Fire once right away so a just-launched iPad gets fresh content.
  prefetchGuestKiosk();
  backgroundTimerId = setInterval(() => { prefetchGuestKiosk(); }, intervalMs);
  const onVis = () => { if (document.visibilityState === "visible") prefetchGuestKiosk(); };
  document.addEventListener("visibilitychange", onVis);
  return () => {
    document.removeEventListener("visibilitychange", onVis);
    stopGuestBackgroundSync();
  };
}

export function stopGuestBackgroundSync() {
  if (backgroundTimerId) {
    clearInterval(backgroundTimerId);
    backgroundTimerId = null;
  }
}
